import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MATERIAL_IMPORTS } from '../../shared-exports/material.exports';
import { ClickOutsideDirective } from '../../directives/click-outside';
import { DatePipe } from '@angular/common';
import { AuthService } from '../../../services/auth-service';
import { AppStoreService } from '../../../store/ngrx-signal.store';
import { ENTITIES } from '../../../store/entities.registry';
import { LOGGED_IN_USER, LoginUserResponse } from '../../../modules/user-profile/user-profile.models';
import { Apollo } from 'apollo-angular';
import { GET_NOTIFICATIONS, GET_UNREAD_NOTIFICATION_COUNT, MARK_ALL_NOTIFICATIONS_AS_READ, MARK_NOTIFICATION_AS_READ } from '../top-bar/top-bar.graphql';
import { firstValueFrom } from 'rxjs';

interface BreadcrumbItem {
  label?: string;
  icon?: string;
  route?: string;
  command?: (item: BreadcrumbItem) => void;
}

@Component({
  selector: 'app-breadcrumb',
  imports: [RouterLink, ...MATERIAL_IMPORTS, ClickOutsideDirective],
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.scss',
  standalone: true,
  providers: [DatePipe]
})
export class Breadcrumb implements OnInit {
  @Input() breadcrumbs: any;
  @Input() parentRoute: any;

  // Signals for state management
  isNotificationOpen = signal(false);
  isProfileOpen = signal(false);
  leaving = signal(false);
  isFullscreen = signal(false);
  hasNewSettings = signal(true);

  unreadCount = signal(0);
  notifications = signal<any[]>([]);

  private readonly store = inject(AppStoreService);
  private readonly apollo = inject(Apollo);
  private readonly datePipe = inject(DatePipe);

  userDetails = this.store.entitySingleSignal<LoginUserResponse['me']['data']>(ENTITIES.LOGIN_USER);

  user = computed(() => {
    const data = this.userDetails();
    return {
      name: data ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : 'User',
      email: data ? data.email : '',
      role: data ? data.role?.name || '' : '',
      avatarUrl: '',
    };
  });

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {
    this.store.query<LoginUserResponse>(ENTITIES.LOGIN_USER, LOGGED_IN_USER);
  }

  ngOnInit() {
    this.loadUnreadCount();
    this.loadNotifications();
  }

  navigateHome() {
    this.router.navigate(['/']);
  }

  async loadUnreadCount() {
    try {
      const result = await firstValueFrom(
        this.apollo.query<any>({
          query: GET_UNREAD_NOTIFICATION_COUNT,
          fetchPolicy: 'network-only'
        })
      );
      if (result.data?.getUnreadNotificationCount?.data !== undefined) {
        this.unreadCount.set(result.data.getUnreadNotificationCount.data);
      }
    } catch (e) {
      console.error('Failed to load unread notification count', e);
    }
  }

  async loadNotifications() {
    try {
      const result = await firstValueFrom(
        this.apollo.query<any>({
          query: GET_NOTIFICATIONS,
          variables: { filtering: {} },
          fetchPolicy: 'network-only'
        })
      );
      const data = result.data?.getNotifications?.data || [];
      this.notifications.set(data
        .map((n: any) => ({
          id: n.uniqueId || n.id,
          title: n.title,
          message: n.body,
          time: this.datePipe.transform(n.createdAt, 'medium') || 'Unknown',
          type: n.notificationType?.toLowerCase() || 'info',
          icon: this.getIconForType(n.notificationType),
          read: n.isRead,
          actionUrl: n.actionUrl,
        }))
        .filter((n: any) => !n.read)
      );
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  }

  getIconForType(type: string): string {
    switch (type?.toLowerCase()) {
      case 'success': return 'check_circle';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'notifications';
    }
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          this.isFullscreen.set(true);
        })
        .catch((err) => {
          console.error('Error attempting to enable fullscreen:', err);
        });
    } else {
      document.exitFullscreen().then(() => {
        this.isFullscreen.set(false);
      });
    }
  }

  async handleNotificationClick(notification: any) {
    if (!notification.read) {
      await this.markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      this.isNotificationOpen.set(false);
      
      let routePath = notification.actionUrl;
      try {
        let urlString = routePath;
        // If it looks like a domain without a protocol, prepend http:// so URL parser works
        if (!urlString.startsWith('http://') && !urlString.startsWith('https://')) {
            if (urlString.includes('localhost') || urlString.match(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\//)) {
                urlString = 'http://' + urlString;
            } else {
                // It's a normal relative path, use a dummy base to safely parse it
                urlString = 'http://dummy.com' + (urlString.startsWith('/') ? '' : '/') + urlString;
            }
        }
        
        const urlObj = new URL(urlString);
        
        const queryParams: Record<string, string> = {};
        urlObj.searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });

        this.router.navigate([urlObj.pathname], {
          queryParams,
          fragment: urlObj.hash ? urlObj.hash.substring(1) : undefined,
          state: { data: notification, notification }
        }).then(success => {
          if (!success) {
            console.warn('Router failed to navigate to:', urlObj.pathname);
          }
        });
      } catch (e) {
        console.error('Invalid URL parsing', e);
        this.router.navigate(['/' + notification.actionUrl.replace(/^\/+/, '')], {
          state: { data: notification, notification }
        });
      }
    }
  }

  async markAsRead(id: string) {
    try {
      await firstValueFrom(
        this.apollo.mutate({
          mutation: MARK_NOTIFICATION_AS_READ,
          variables: { uniqueId: id }
        })
      );
      this.notifications.update((notifs) =>
        notifs.filter((n) => n.id !== id)
      );
      this.unreadCount.update((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error('Failed to mark notification as read', e);
    }
  }

  async markAllAsRead(event?: Event) {
    event?.stopPropagation();
    try {
      await firstValueFrom(
        this.apollo.mutate({
          mutation: MARK_ALL_NOTIFICATIONS_AS_READ
        })
      );
      this.notifications.set([]);
      this.unreadCount.set(0);
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.isProfileOpen.set(false);
    this.isNotificationOpen.update((v) => !v);
  }

  closeAll() {
    this.isNotificationOpen.set(false);
    this.isProfileOpen.set(false);
  }

  toggleProfile(event?: Event) {
    event?.stopPropagation();

    this.isNotificationOpen.set(false);

    if (this.isProfileOpen()) {
      this.leaving.set(true);

      setTimeout(() => {
        this.leaving.set(false);
        this.isProfileOpen.set(false);
      }, 150);
    } else {
      this.isProfileOpen.set(true);
    }
  }

  async logout() {
    this.isProfileOpen.set(false);
    await this.authService.logout();
  }
}
